import React from 'react';

// Section header component
export function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      {count !== undefined && (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400">
          {count}
        </span>
      )}
    </div>
  );
}

// Label-value pair component
export function LabelValue({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-white font-medium">{value}</p>
    </div>
  );
}

// Load Button component
export function LoadButton({
  label,
  onClick,
  isLoading,
  isLoaded,
  disabled,
}: {
  label: string;
  onClick: () => void;
  isLoading: boolean;
  isLoaded: boolean;
  disabled?: boolean;
}) {
  if (isLoaded) return null;
  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#6d7cff] to-[#e866dd] text-white font-medium rounded-lg transition disabled:opacity-50 ${disabled ? 'pointer-events-none' : 'hover:opacity-90'}`}
    >
      {isLoading ? (
        <>
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading...
        </>
      ) : (
        label
      )}
    </button>
  );
}

// No Data Message component
export function NoDataMessage({ message = 'No records found for this vehicle' }: { message?: string }) {
  return (
    <p className="text-gray-500 text-center py-4">{message}</p>
  );
}

// Helper to check if a value should be displayed (not N/A, empty, etc.)
export function hasValue(val: string | undefined | null): val is string {
  if (!val) return false;
  const trimmed = val.trim().toLowerCase();
  return trimmed !== '' && trimmed !== 'n/a' && trimmed !== 'no information';
}

// Feature badge component for car features
export function FeatureBadge({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  const isYes = value.toLowerCase() === 'yes';
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${isYes ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
      {label}: {isYes ? '✓' : '✗'}
    </span>
  );
}

// Helper to check if a query returned "no data" (400 response)
export function isNoData(query: { data?: { status?: string } }) {
  return query.data?.status === 'no_data';
}
